import React, { useState, useEffect, useMemo } from 'react';
import { observer, inject } from 'mobx-react';
import _, { set } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import { routes } from 'env/routes';
import { withTranslation } from 'react-i18next';
import { linkTo } from 'env/utils/strings';
import withCustomComponent from 'ui/components/withCustomComponent';
import ActiveLink from 'ui/components/ActiveLink';
import DateInputField from 'ui/components/forms/DateInputField';
import { bookingsFeatures } from 'env/bookingsResources';
import { TimeInputField } from 'ui/components/forms/TimeInputFieldBW';
import { syncShifts, formatShiftTitle } from 'env/utils/shiftsHelper';
import { useBusinessesWithResources } from '../../../../env/hooks/useBusinessesWithResources';
import { useRootBusinesses } from '../../../../env/hooks/useRootBusinesses';
import { usePaymentRoutes } from "../../../../env/hooks/payments/usePaymentRoutes";
import {
  homeHasTreatmentRooms,
  isTreatmentRoomsGroup,
} from 'env/utils/treatmentRoomsGroup';

const BookingHeader = withCustomComponent('BookingHeader')(
  withTranslation()(
    inject(
      'appStore',
      'authStore',
      'bookingsStore',
      'floorPlanStore',
      'checkoutStore'
    )(
      observer((props) => {
        const {
          t,
          appStore,
          authStore,
          bookingsStore,
          floorPlanStore,
          checkoutStore,
          componentName,
          doSearch,
          selectShift,
          selectDates,
          shifts,
          isLoadingItems,
          shiftId,
          fromTime,
          toTime,
          hideDivider,
          hideResourceFilters = false,
          hideLocationFilter,
        } = props;
        const { configuration, businesses } = appStore;
        const { basket } = checkoutStore;
        const { resourceGroups, resourceSearch, resources } = bookingsStore;
        const { floorPlans } = floorPlanStore;
        const router = useRouter();
        const homeBusinessId = authStore?.customer?.InvoicingBusinessId;
        const hasTreatmentRooms = useMemo(
          () => homeHasTreatmentRooms(homeBusinessId, resources),
          [resources, homeBusinessId],
        );
        const treatmentRoomsSelected = isTreatmentRoomsGroup(router.query.group);
        const paymentRoutes = usePaymentRoutes();
        const [filtersExpanded, setFiltersExpanded] = useState(false);
        const [availabilityExpanded, setAvailabilityExpanded] = useState(false);
        const [allocation, setAllocation] = useState(
          router?.query?.allocation ?? ''
        );
        const [feature, setFeature] = useState(router?.query?.feature ?? '');

        useEffect(() => {
          appStore.loadBusinesses();
          bookingsStore.loadResources();
        }, []);

        const memorisedResourceList = useMemo(() => {
          if (!!router.query.group) {
            return resources.filter(
              (resource) => resource.GroupName === router.query.group
            );
          }
          return resources;
        }, [resources, router.query.group]);

        const businessesWithResources = useRootBusinesses(
          useBusinessesWithResources(memorisedResourceList),
          businesses
        ).map((b) => b.children);

        const handleSearchBySelectChange = (e) => {
          // Room features are queried as "proyector=true&catering=true"...
          // Resource and types are queried as type=123456&group=Rooms...

          //TODO: consider the option to filter by multiple features as in the app.
          const href =
            e.target.name === 'feature'
              ? linkTo(
                  router,
                  bookingsFeatures(t).reduce((acc, cur) => {
                    acc[cur.key] =
                      cur.key === e.target.value && e.target.value
                        ? 'true'
                        : null;
                    return acc;
                  }, {})
                )
              : linkTo(router, {
                  [e.target.name]: e.target.value,
                });
          e.preventDefault();
          router.push(href);
        };

        const filtersToggle = () => {
          setFiltersExpanded(!filtersExpanded);
        };

        const availabilityToggle = () => {
          setAvailabilityExpanded(!availabilityExpanded);
          // shows availablity in resource cards
          if (props.availabilityToggle) props.availabilityToggle();
        };

        const updateByShift = (shiftId) => {
          selectShift(shiftId);
          doSearch();
        };

        const updateDates = (name, value, skipShift) => {
          const { shifts, toTime, fromTime } = props;
          const updateObj = {};
          if (name === 'fromTime' && !skipShift) {
            const diff = moment(value).diff(fromTime, 'minutes');
            updateObj.toTime = moment(toTime).add(diff, 'minutes').toDate();
          }

          updateObj[name] = value;
          selectDates(updateObj);

          name === 'fromTime'
            ? selectShift(syncShifts(shifts, value, toTime))
            : selectShift(syncShifts(shifts, fromTime, value));

          doSearch(true);
        };

        const showAvailability = true;
        const showEndDate = configuration['Bookings.ShowEndDate'];
        const hasShifts = shifts ? shifts.length > 0 : false;
        const dateToday = new Date();
        const minHour = moment(fromTime).isSame(dateToday, 'day')
          ? moment(dateToday).minute() > 30
            ? moment(dateToday).set('minutes', 60).toDate()
            : moment(dateToday).set('minutes', 30).toDate()
          : moment(dateToday).startOf('day').toDate();
        const featuresToShow = bookingsFeatures(t).filter(
          (feature) =>
            resourceSearch.Resources.find((r) => r[feature.key]) != null
        );

        return (
          <div data-component-name={componentName} className="booking-header">
            <div className="d-block d-md-flex align-items-center justify-content-between">
              <div className="">
                <h2 className="mb-0">{t('Available resources')}</h2>
              </div>
              <div className="d-flex d-md-block justify-content-between mt-20 mt-md-0">
                <ActiveLink
                  href={routes.resources}
                  activeOnRootPath={true}
                  legacyBehavior
                >
                  <a className="btn btn-sm btn-icon btn-gray-light btn-md-xs">
                    <i className="icon-card-view d-none d-lg-block"></i>
                    <span className="pl-4 pl-lg-0">{t('Card view')}</span>
                  </a>
                </ActiveLink>
                {/* <ActiveLink href={routes.calendar} activeOnRootPath={true}>
              <a className="btn btn-sm btn-icon btn-gray-light ml-0 ml-md-8 btn-md-xs">
                <i className="icon-calendar-view d-none d-lg-block"></i>
                <span className="pl-4 pl-lg-0">{t('Calendar view')}</span>
              </a>
            </ActiveLink> */}
                {floorPlans.length > 0 && (
                  <ActiveLink
                    href={routes.floorplan}
                    activeOnRootPath={true}
                    legacyBehavior
                  >
                    <a className="btn btn-sm btn-icon btn-gray-light ml-0 ml-md-8 btn-md-xs">
                      <i className="icon-floorplan-view d-none d-lg-block"></i>
                      <span className="pl-4 pl-lg-0">
                        {t('Floor plan view')}
                      </span>
                    </a>
                  </ActiveLink>
                )}
                <ActiveLink
                  href={routes.resources_map}
                  activeOnRootPath={true}
                  legacyBehavior
                >
                  <a
                    id="map_link"
                    className="btn btn-sm btn-icon btn-gray-light ml-0 ml-md-8 btn-md-xs"
                  >
                    <i className="icon-location d-none d-lg-block"></i>
                    <span className="pl-4 pl-lg-0">{t('Map')}</span>
                  </a>
                </ActiveLink>
              </div>
            </div>

            {!hideResourceFilters && (
              <div className="mt-20">
                <ul className="list-inline-32 sm-row">
                  <li
                    id="filter-button"
                    className="ml-md-auto d-block d-md-none"
                  >
                    <button
                      onClick={() => filtersToggle()}
                      className="btn btn-icon btn-sm btn-outline"
                    >
                      {filtersExpanded ? (
                        <>
                          <i className="icon-close"></i>
                          <span>{t('Hide filters')}</span>
                        </>
                      ) : (
                        <>
                          <i className="icon-add"></i>
                          <span>{t('Filters')}</span>
                        </>
                      )}
                    </button>
                  </li>

                  <>
                    <li
                      id="capacity-filter"
                      className={`d-none align-items-center w-sm-100 d-md-flex ${
                        filtersExpanded ? 'd-flex' : ''
                      }`}
                    >
                      <label
                        className="text-gray-700 fs-14 mr-12 mw-sm-64px"
                        htmlFor="select-capacity"
                      >
                        {t('Capacity:')}
                      </label>
                      <select
                        name="allocation"
                        id="select-capacity"
                        value={allocation ?? ''}
                        onChange={(e) => {
                          handleSearchBySelectChange(e);
                          setAllocation(e.target.value);
                        }}
                        className="custom-select custom-select-sm w-sm-100"
                      >
                        {[1, 2, 5, 10, 20, 50].map((i) => (
                          <option key={i} value={i}>
                            {t('{{capacity}} or more', { capacity: i })}
                          </option>
                        ))}
                      </select>
                    </li>
                    <li
                      id="resource-filter"
                      className={`d-none align-items-center w-sm-100 d-md-flex ${
                        filtersExpanded ? 'd-flex' : ''
                      }`}
                    >
                      <label
                        className="text-gray-700 fs-14 mr-12 mw-sm-64px"
                        htmlFor="select-resource-group"
                      >
                        {t('Resource:')}
                      </label>
                      <select
                        name="group"
                        id="select-resource-group"
                        value={router.query.group ?? ''}
                        onChange={handleSearchBySelectChange}
                        className="custom-select custom-select-sm w-sm-100"
                      >
                        <option value="">{t('All')}</option>
                        {resourceGroups
                          .filter((g) => g.groupName)
                          .filter(
                            (g) =>
                              !isTreatmentRoomsGroup(g.groupName) ||
                              hasTreatmentRooms,
                          )
                          .sort((a, b) =>
                            a.groupName.localeCompare(b.groupName)
                          )
                          .map((group, i) => (
                            <option key={i} value={group.groupName}>
                              {t(group.groupName)}
                            </option>
                          ))}
                      </select>
                    </li>
                    {!hideLocationFilter && !treatmentRoomsSelected && (
                      <li
                        id="location-filter"
                        className={`d-none align-items-center w-sm-100 d-md-flex ${
                          filtersExpanded ? 'd-flex' : ''
                        }`}
                      >
                        <label
                          className="text-gray-700 fs-14 mr-12 mw-sm-64px"
                          htmlFor="select-location"
                        >
                          {t('Location:')}
                        </label>
                        <select
                          name="businessId"
                          id="select-location"
                          value={router.query.businessId ?? ''}
                          onChange={handleSearchBySelectChange}
                          className="custom-select custom-select-sm w-sm-100"
                        >
                          {businessesWithResources[0]?.length > 0 && (
                            <>
                              <option value="">{t('All')}</option>
                              {businessesWithResources[0]
                                .filter((b) => b.children.length > 0)
                                .reduce((array, b) => {
                                  array.push(b);
                                  if (b.children.length) {
                                    return array.concat(
                                      b.children.map((c) => c)
                                    );
                                  }
                                  return array;
                                }, [])
                                .filter((b) => b.business.Visible)
                                .map((b) => (
                                  <option
                                    key={b.businessId}
                                    value={b.businessId}
                                  >
                                    {b.children.length > 0
                                      ? b.name
                                      : '- ' + b.name}
                                  </option>
                                ))}
                            </>
                          )}
                        </select>
                      </li>
                    )}

                    {featuresToShow.length > 0 && (
                      <li
                        id="features-filter"
                        className={`d-none align-items-center w-sm-100 d-md-flex ${
                          filtersExpanded ? 'd-flex' : ''
                        }`}
                      >
                        <span className="text-gray-700 fs-14 mr-12 mw-sm-64px">
                          {t('Features:')}
                        </span>
                        <select
                          name="feature"
                          value={feature ?? ''}
                          onChange={(e) => {
                            handleSearchBySelectChange(e);
                            setFeature(e.target.value);
                          }}
                          className="custom-select custom-select-sm w-sm-100"
                        >
                          <option value="">{t('All')}</option>
                          {featuresToShow.map((feature, i) => (
                            <option key={i} value={feature.key}>
                              {feature.name}
                            </option>
                          ))}
                        </select>
                      </li>
                    )}
                  </>

                  {showAvailability && (
                    <li id="filter-search-button" className="ml-md-auto">
                      <button
                        onClick={() => availabilityToggle()}
                        className="btn btn-icon btn-sm btn-outline"
                      >
                        <i className="icon-search"></i>
                        <span>Find available spaces</span>
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {hideResourceFilters ||
            (showAvailability && availabilityExpanded) ? (
              <div className="row mt-20">
                <div className="col">
                  <div className="bg-gray-200 p-20 rounded">
                    <form className="d-flex flex-column flex-md-row">
                      <div className="w-100">
                        <ul className="list-inline-32 sm-column">
                          <li className="d-flex align-items-center w-sm-100">
                            <DateInputField
                              className="row sm-72-100"
                              label={showEndDate ? t('Start date') : t('Date')}
                              name="fromTime"
                              value={fromTime}
                              onChange={updateDates}
                              customProps={{
                                min: configuration[
                                  'Bookings.AcceptPastBookings'
                                ]
                                  ? new Date(1900, 0, 1)
                                  : new Date(),
                              }}
                            />
                          </li>
                          <li className="d-flex align-items-center w-sm-100">
                            <TimeInputField
                              classes="row sm-72-100"
                              label={showEndDate ? t('Start time') : t('Start')}
                              name="fromTime"
                              value={fromTime}
                              onChange={(name, value) =>
                                updateDates(name, value, false)
                              }
                              allowPastDates={
                                configuration['Bookings.AcceptPastBookings']
                              }
                            />
                          </li>
                          {showEndDate && (
                            <li className="d-flex align-items-center w-sm-100">
                              <DateInputField
                                className="row sm-72-100"
                                label={t('End date')}
                                name="toTime"
                                value={toTime}
                                onChange={updateDates}
                                customProps={{
                                  min: configuration[
                                    'Bookings.AcceptPastBookings'
                                  ]
                                    ? new Date(1900, 0, 1)
                                    : new Date(),
                                }}
                              />
                            </li>
                          )}
                          <li className="d-flex align-items-center w-sm-100">
                            <TimeInputField
                              classes="row sm-72-100"
                              label={showEndDate ? t('End time') : t('End')}
                              name="toTime"
                              value={toTime}
                              onChange={updateDates}
                              allowPastDates={
                                configuration['Bookings.AcceptPastBookings']
                              }
                            />
                          </li>
                        </ul>
                        {hasShifts && (
                          <ul className="list-inline-32 sm-row preset-row">
                            <label className="control-label">
                              {t('Preset:')}
                            </label>
                            {shifts.map((item, i) => (
                              <li
                                className="d-flex align-items-center w-sm-100"
                                key={i}
                              >
                                <button
                                  className={shiftId == item.id ? 'active' : ''}
                                  value={item.id}
                                  onClick={(ev) => {
                                    updateByShift(item.id);
                                    ev.preventDefault();
                                  }}
                                >
                                  {t(formatShiftTitle(item.title))}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="d-flex flex-column ml-md-8">
                        {basket.length > 1 && (
                          <button
                            onClick={(ev) => {
                              router.push(paymentRoutes.checkout);
                            }}
                            className="d-block w-100 btn btn-icon btn-sm w-sm-100 mt-8"
                            type="button"
                          >
                            <i className="icon-cart"></i>
                            {t('Checkout')}
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            ) : (
              !hideDivider && <hr className="mt-24 mb-0 d-md-block d-none" />
            )}
          </div>
        );
      })
    )
  )
);

export default BookingHeader;
